import { useCallback, useMemo } from 'react'
import { useExploreIdFromPathname, useSpaceIdFromPathname } from 'hooks/useSpaceInfoFromPathname'
import { JoinFlowStatus } from 'use-towns-client'
import { create } from 'zustand'
import { useShallow } from 'zustand/react/shallow'

import { useJoinAfterSuccessfulCrossmint } from './hooks/useJoinAfterSuccessfulCrossmint'
import { useJoinTown } from './hooks/useJoinTown'
import { useStartJoinDoesNotMeetRequirements } from './hooks/useStartJoinDoesNotMeetRequirements'
import { useStartJoinMeetsRequirements } from './hooks/useStartJoinMeetsRequirements'

const JOIN = 'join'
const PRIVY_OAUTH_STATE = 'privy_oauth_state'

export function usePublicPageLoginFlow() {
    const spaceIdFromPathname = useSpaceIdFromPathname()
    const exploreIdFromPathname = useExploreIdFromPathname()

    const spaceId = exploreIdFromPathname ?? spaceIdFromPathname

    const startJoinMeetsRequirements = useStartJoinMeetsRequirements({ spaceId })
    const startJoinDoesNotMeetRequirements = useStartJoinDoesNotMeetRequirements({ spaceId })
    const joinTown = useJoinTown({ spaceId })
    const joinAfterSuccessfulCrossmint = useJoinAfterSuccessfulCrossmint({ spaceId })

    const {
        spaceBeingJoined,
        setSpaceBeingJoined,
        setJoinStatusStep,
        joinStatusStep,
        endPublicPageLoginFlow,
        disableJoinUi,
        setDisableJoinUi,
        assetModal,
        setAssetModal,
    } = publicPageLoginStore(
        useShallow((s) => ({
            assetModal: s.assetModal,
            disableJoinUi: s.disableJoinUi,
            setDisableJoinUi: s.setDisableJoinUi,
            setAssetModal: s.setAssetModal,
            spaceBeingJoined: s.spaceBeingJoined,
            joinStatusStep: s.joinStatusStep,
            setSpaceBeingJoined: s.setSpaceBeingJoined,
            setJoinStatusStep: s.setJoinStatusStep,
            endPublicPageLoginFlow: s.endPublicPageLoginFlow,
        })),
    )

    const startJoinPreLogin = useCallback(() => {
        if (!spaceId || spaceBeingJoined) {
            return
        }
        setSpaceBeingJoined(spaceId)
    }, [spaceBeingJoined, setSpaceBeingJoined, spaceId])

    return useMemo(
        () => ({
            startJoinPreLogin,
            startJoinMeetsRequirements,
            joinTown,
            endPublicPageLoginFlow,
            spaceBeingJoined,
            joinStatusStep,
            setJoinStatusStep,
            startJoinDoesNotMeetRequirements,
            disableJoinUi,
            setDisableJoinUi,
            joinAfterSuccessfulCrossmint,
            assetModal,
            setAssetModal,
        }),
        [
            startJoinPreLogin,
            startJoinMeetsRequirements,
            joinTown,
            endPublicPageLoginFlow,
            spaceBeingJoined,
            joinStatusStep,
            setJoinStatusStep,
            disableJoinUi,
            setDisableJoinUi,
            joinAfterSuccessfulCrossmint,
            assetModal,
            setAssetModal,
            startJoinDoesNotMeetRequirements,
        ],
    )
}

const params = new URLSearchParams(window.location.search)

// using searchParams from react-router-dom navigates and the params from privy redirect on login get wried and buggy
// https://github.com/remix-run/react-router/discussions/9851
function updateQueryStringValueWithoutNavigation(queryKey: string, queryValue: string) {
    const currentSearchParams = new URLSearchParams(window.location.search)
    const oldQuery = currentSearchParams.get(queryKey) ?? ''
    if (queryValue === oldQuery) {
        return
    }

    if (queryValue) {
        currentSearchParams.set(queryKey, queryValue)
    } else {
        currentSearchParams.delete(queryKey)
    }
    const newUrl = [window.location.pathname, currentSearchParams.toString()]
        .filter(Boolean)
        .join('?')
    window.history.replaceState(null, '', newUrl)
}

export const publicPageLoginStore = create<{
    /**
     * the space that is being joined
     * indicates a user has clicked join/login and the process is kicking off
     */
    spaceBeingJoined: string | undefined
    /**
     * whether the join ui is disabled
     * prevent ui interaction during things like crossmint payment
     */
    disableJoinUi: boolean
    /**
     * whether the asset (gating) modal is open
     */
    assetModal: boolean
    /**
     * the current step of the join animation w/ the globe
     */
    joinStatusStep: JoinFlowStatus | undefined
    setDisableJoinUi: (disableJoinUi: boolean) => void
    setAssetModal: (assetModal: boolean) => void
    setSpaceBeingJoined: (isJoining: string) => void
    setJoinStatusStep: (step: JoinFlowStatus | undefined) => void
    /**
     * endPublicPageLoginFlow
     * kills the join flow
     * this should be called:
     * - if any errors or cancellations occur during the join flow
     * - via MonitorJoinFlow for a successful join
     *      - calling this at a specific point during join flow (like after a user has joined in the protocol) does not make for great UI b/c the rest of the app waits for memberships to be synced
     *      - therefore we wait for similar state in MonitorJoinFlow and call this at that point
     */
    endPublicPageLoginFlow: () => void
}>((set) => ({
    joinStatusStep: undefined,
    disableJoinUi: false,
    assetModal: false,
    setDisableJoinUi: (disableJoinUi) => set({ disableJoinUi }),
    setAssetModal: (assetModal) => set({ assetModal }),
    setJoinStatusStep: (joinStep) => set({ joinStatusStep: joinStep }),
    spaceBeingJoined:
        // check privy oauth state also to prevent getting into join flow if user refreshes in the middle of the flow
        params.has(PRIVY_OAUTH_STATE) && params.has(JOIN)
            ? params.get(JOIN)?.toString()
            : undefined,
    setSpaceBeingJoined: (joiningSpaceId) => {
        updateQueryStringValueWithoutNavigation(JOIN, joiningSpaceId)
        set({ spaceBeingJoined: joiningSpaceId })
    },
    endPublicPageLoginFlow: () => {
        updateQueryStringValueWithoutNavigation(JOIN, '')
        set({
            spaceBeingJoined: undefined,
            joinStatusStep: undefined,
            assetModal: false,
            disableJoinUi: false,
        })
    },
}))
